import { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { Spinner, Table } from "react-bootstrap";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import {
  getVisitorTrackingList,
  visitorTrackingListExcel,
  visitorTrackingListPdf,
} from "../../services/api/admin";
import { getMonthYearOptions, monthNames } from "./visitorTrackingUtils";
import AdminDashboard from "../../pages/Admin/AdminDashboard/AdminDashboard";
import { Message } from "../message";
import { HomeIcon } from "../Application/ApplicationIcon";
import TotalRecords from "../notFound/TotalRecords";
import { EyeIcon } from "../../pages/Admin/AdminIcon";
import NotFoundTable from "../notFound/NotFoundTable";
import { formatDateWithSlash, printData } from "../../Utils/common";
import { stateDistrict } from "../../services/api/auth";
import { PdfBaseUrl } from "../../Utils/Utils";

const AdminVisitorTracking = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const [trackingList, setTrackingList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0); // Total number of pages
  const [orderBy, setOrderBy] = useState("asc");
  const [apiMessage, setApiMessage] = useState({ type: "", message: "" });
  const [tableDataLoaded, setTableDataLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState();
  const [sortBy, setSortBy] = useState("district");
  const [searchData, setSearchData] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [pdfDownLoading, setPdfDownLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [monthsList, setMonthsList] = useState([]);
  const [searchMonth, setSearchMonth] = useState();
  const [districtList, setDistrictList] = useState([]);
  const [searchDistrict, setSearchDistrict] = useState();

  useEffect(() => {
    visitorTrackingList("created_at", "1", "desc", "", "", "");
    setMonthsList(getMonthYearOptions());
    loadDistricts();
  }, []);

  const resetFilter = () => {
    setSearchMonth("");
    setSearchDistrict("");
    visitorTrackingList(sortBy, 1, orderBy, searchData, "", "");
  };

  const loadDistricts = async () => {
    try {
      const response = await stateDistrict();
      const data = response.data.data.map((district) => ({
        label: district.name_en,
        value: district.id,
      }));
      setDistrictList(data);
    } catch (error) {
      setApiMessage({ type: "error", message: error?.message });
    }
  };

  const visitorTrackingList = async (
    sortBy,
    pageNo,
    orderBy,
    searchBy,
    searchedMonth,
    searchedDistrict
  ) => {
    try {
      setSearchData(searchBy);
      setOrderBy(orderBy);
      setSortBy(sortBy);
      const visitorTrackingListData = await getVisitorTrackingList(
        sortBy,
        orderBy,
        searchBy,
        pageNo,
        searchedMonth?.value ? searchedMonth?.value?.split(",")[0] : "",
        searchedMonth?.value ? searchedMonth?.value?.split(",")[1] : "",
        searchedDistrict?.value ? searchedDistrict?.value : ""
      );
      if (visitorTrackingListData.status == 200) {
        const data = visitorTrackingListData?.data?.data;
        setTotalPages(
          Math.ceil(
            visitorTrackingListData?.data?.total /
              visitorTrackingListData?.data?.per_page
          )
        );
        setCurrentPage(pageNo > 0 ? pageNo - 1 : pageNo);
        setTrackingList(data);
        setTotalRecords(
          visitorTrackingListData?.data?.total
            ? visitorTrackingListData?.data?.total
            : 0
        );
      } else {
        setTrackingList([]);
        setTotalRecords(0);
        setApiMessage({
          type: "error",
          message: visitorTrackingListData?.message,
        });
      }
      setTableDataLoaded(true);
    } catch (err) {
      setTotalRecords(0);
      setApiMessage({ type: "error", message: err?.message });
      setTableDataLoaded(true);
    }
  };

  const handlePageChange = (selectedPage) => {
    visitorTrackingList(
      sortBy,
      selectedPage.selected + 1,
      orderBy,
      searchData,
      searchMonth,
      searchDistrict
    ); // Increment by 1 if the API starts page numbers from 1
  };

  const pdfDownload = async (searchedMonth, searchedDistrict) => {
    try {
      setPdfDownLoading(true);
      const response = await visitorTrackingListPdf(
        searchedMonth?.value ? searchedMonth?.value?.split(",")[0] : "",
        searchedMonth?.value ? searchedMonth?.value?.split(",")[1] : "",
        searchedDistrict?.value ? searchedDistrict?.value : ""
      );
      setPdfDownLoading(false);
      window.open(
        `${PdfBaseUrl()}/${response.folder_path}/${response.file_name}`
      );
    } catch (err) {
      setPdfDownLoading(false);
      setApiMessage({ type: "error", message: err?.message });
    }
  };

  const excelDownload = async (searchedMonth, searchedDistrict) => {
    try {
      const response = await visitorTrackingListExcel(
        searchedMonth?.value ? searchedMonth?.value?.split(",")[0] : "",
        searchedMonth?.value ? searchedMonth?.value?.split(",")[1] : "",
        searchedDistrict?.value ? searchedDistrict?.value : ""
      );
      window.open(response?.file_path);
    } catch (err) {
      setApiMessage({ type: "error", message: err?.message });
    }
  };

  const exportPrint = async (searchedMonth, searchedDistrict) => {
    try {
      const response = await visitorTrackingListPdf(
        searchedMonth?.value ? searchedMonth?.value?.split(",")[0] : "",
        searchedMonth?.value ? searchedMonth?.value?.split(",")[1] : "",
        searchedDistrict?.value ? searchedDistrict?.value : ""
      );
      if (response) {
        printData(response);
      }
    } catch (err) {
      setApiMessage({ type: "error", message: err?.message });
    }
  };

  return (
    <AdminDashboard>
      <div className="aod-outer">
        {apiMessage && (
          <Message
            apiMessage={apiMessage}
            setApiMessage={setApiMessage}
          ></Message>
        )}
        <div className="ap-breadcrumb pt-2 pb-2 mb-2 adm-breadcrumb">
          <div>
            <HomeIcon />
            <b onClick={() => navigate("/admin/")}>Home /</b>{" "}
            <span>Hotel Visitors Tracking</span>
          </div>
          <div className="dropdown download_file_dropdown ms-auto d-flex gap-2 mb-3 flex-wrap align-items-center">
            {pdfDownLoading ? (
              <a className=" px-2 btn btn-danger btn-sm">
                {" "}
                <i className="fa fa-file-pdf me-1"></i>
                <span>PDF</span>
                <Spinner
                  animation="border"
                  className="pdf-spinner"
                  role="status"
                  style={{ marginLeft: "5px" }}
                >
                  <span className="visually-hidden"></span>
                </Spinner>
              </a>
            ) : (
              <a
                className=" px-2 btn btn-danger btn-sm"
                onClick={() => pdfDownload(searchMonth, searchDistrict)}
              >
                {" "}
                <i className="fa fa-file-pdf me-1"></i> PDF
              </a>
            )}
            <a
              className=" px-2 btn btn-success btn-sm"
              onClick={() => excelDownload(searchMonth, searchDistrict)}
            >
              <i className="fa fa-file-excel  me-1"></i>
              Excel{" "}
            </a>
            <a
              className=" px-2 btn btn-info  btn-sm"
              onClick={() => exportPrint(searchMonth, searchDistrict)}
            >
              <i className="fa fa-print me-1"></i>
              Print
            </a>
          </div>
        </div>
        <div className="aod-inner pt-0">
          <div className="aod-inner pt-0">
            <div className="aod-bottom">
              <div className="adm-tabs-outer d-flex justify-content-between">
                <h4 className="mb-0">Hotel Visitors Tracking </h4>
                <div className="position-relative w-25">
                  <input
                    className="px-2 form-control"
                    placeholder="Search"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      visitorTrackingList(
                        sortBy,
                        1,
                        orderBy,
                        e.target.value,
                        searchMonth,
                        searchDistrict
                      );
                    }}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-clear position-absolute"
                      onClick={(e) => {
                        setSearchTerm("");
                        visitorTrackingList(
                          sortBy,
                          1,
                          orderBy,
                          e.target.value,
                          searchMonth,
                          searchDistrict
                        );
                      }}
                      style={{
                        top: "50%",
                        right: "10px",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "transparent",
                        fontSize: "1rem",
                        cursor: "pointer",
                      }}
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
              <div className="aod-head"></div>
              <div className="row py-4">
                <div className="col-md-4 aod-resp-tab">
                  <div className="suf-input-box aod-form-input mb-2">
                    <Select
                      className="menu_open_select w-100"
                      classNamePrefix="ad-menu_open_select_class"
                      aria-label="Default select example"
                      value={searchMonth}
                      options={monthsList}
                      onChange={(e) => {
                        setSearchMonth(e);
                        visitorTrackingList(
                          sortBy,
                          1,
                          orderBy,
                          searchData,
                          e,
                          searchDistrict
                        );
                      }}
                      placeholder="Select Report Month"
                    />
                  </div>
                </div>
                <div className="col-md-4 aod-resp-tab">
                  <div className="suf-input-box aod-form-input mb-2">
                    <Select
                      className="menu_open_select w-100"
                      classNamePrefix="ad-menu_open_select_class"
                      aria-label="Default select example"
                      value={searchDistrict}
                      options={districtList}
                      onChange={(e) => {
                        setSearchDistrict(e);
                        visitorTrackingList(
                          sortBy,
                          1,
                          orderBy,
                          searchData,
                          searchMonth,
                          e
                        );
                      }}
                      placeholder="Select District"
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <button
                    onClick={resetFilter}
                    className="ad-reset-btn w-auto px-4"
                  >
                    Reset
                  </button>
                </div>
                <TotalRecords totalRecords={totalRecords}></TotalRecords>
              </div>
              <div className="ad-reg-table">
                <Table size="sm" className="ad-cat-table">
                  <thead>
                    <tr>
                      <th className="ad-sno">S. No</th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "hotel_name",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Hotel Name
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "district_name",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        District
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th className="ad-long">Customer Id</th>
                      <th className="ad-long">Address</th>
                      <th className="ad-long">Mobile Number </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "report_month",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Report Month
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "monthy_total_tourists_indian",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Indian Tourists
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "monthy_nghts_spent_indian",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Indian Nights
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "monthy_total_tourists_foreigner",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Foreigner Tourists
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "monthy_nghts_spent_foreigner",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Foreigner Nights
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th
                        className="ad-long cursor-pointer"
                        onClick={(e) =>
                          visitorTrackingList(
                            "created_at",
                            currentPage + 1,
                            orderBy === "asc" ? "desc" : "asc",
                            searchData,
                            searchMonth,
                            searchDistrict
                          )
                        }
                      >
                        Created At
                        <span>
                          <i className="fa-solid fa-sort"></i>
                        </span>{" "}
                      </th>
                      <th className="ad-long">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingList?.length > 0 ? (
                      trackingList?.map((item, index) => (
                        <tr key={index}>
                          <td>{currentPage * 10 + (index + 1)}</td>
                          <td>{item?.hotel_name || "--"}</td>
                          <td>{item?.district_name || "--"}</td>
                          <td>{item?.unique_id || "--"}</td>
                          <td>{item?.address || "--"}</td>
                          <td>{item?.mobile_number || "--"}</td>
                          <td>{`${monthNames[item?.report_month - 1] || "--"} ${
                            item?.report_year || "--"
                          }`}</td>
                          <td>{item?.monthy_total_tourists_indian ?? "--"}</td>
                          <td>{item?.monthy_nghts_spent_indian ?? "--"}</td>

                          <td>
                            {item?.monthy_total_tourists_foreigner ?? "--"}
                          </td>
                          <td>{item?.monthy_nghts_spent_foreigner ?? "--"}</td>
                          <td>
                            {item?.created_at
                              ? formatDateWithSlash(item?.created_at)
                              : "--"}
                          </td>
                          <td className="icon-col">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`edit-tooltip-${item.id}`}>
                                  View Application Data
                                </Tooltip>
                              }
                            >
                              <span
                                className="ad-cat-edit"
                                onClick={() => {
                                  navigate("/admin/visitor-tracking-view", {
                                    state: {
                                      data: item,
                                    },
                                  });
                                }}
                              >
                                <EyeIcon />
                              </span>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      ))
                    ) : tableDataLoaded ? (
                      <NotFoundTable colSpan="8"></NotFoundTable>
                    ) : (
                      <></>
                    )}
                  </tbody>
                </Table>
                {totalPages > 1 && trackingList?.length > 0 && (
                  <div className="d-flex w-100 justify-content-end">
                    <ReactPaginate
                      previousLabel={"Previous"}
                      nextLabel={"Next"}
                      breakLabel={"..."}
                      pageCount={totalPages} // Total number of pages from API
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={1}
                      onPageChange={handlePageChange} // Method to handle page click
                      forcePage={currentPage}
                      containerClassName={"pagination justify-content-center"} // Custom CSS classes
                      pageClassName={"page-item"}
                      pageLinkClassName={"page-link"}
                      previousClassName={"page-item"}
                      previousLinkClassName={"page-link"}
                      nextClassName={`page-item${
                        !trackingList?.length > 0 ? " disabled" : ""
                      }`}
                      nextLinkClassName={"page-link"}
                      breakClassName={"page-item"}
                      breakLinkClassName={"page-link"}
                      activeClassName={"active"}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminDashboard>
  );
};

export default AdminVisitorTracking;
